import { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { MapPin, Loader2, Save, UserCheck } from 'lucide-react';

export default function TLAssign() {
  const [surveyors, setSurveyors] = useState([]);
  const [myPrecincts, setMyPrecincts] = useState([]);
  const [selectedSurveyor, setSelectedSurveyor] = useState(null);
  const [assignedIds, setAssignedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, p] = await Promise.all([
          API.get('/team-leader/surveyors'),
          API.get('/team-leader/my-precincts'),
        ]);
        setSurveyors(s.data);
        setMyPrecincts(p.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, []);

  const selectSurveyor = (s) => {
    setSelectedSurveyor(s);
    setAssignedIds(s.assigned_precinct_ids || []);
  };

  const togglePrecinct = (pid) => {
    setAssignedIds(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);
  };

  const handleSave = async () => {
    if (!selectedSurveyor) return;
    setSaving(true);
    try {
      await API.post('/team-leader/assign-surveyor', {
        user_id: selectedSurveyor.id,
        ids: assignedIds,
      });
      // Refresh surveyor data
      const { data } = await API.get('/team-leader/surveyors');
      setSurveyors(data);
      const updated = data.find(s => s.id === selectedSurveyor.id);
      if (updated) { setSelectedSurveyor(updated); setAssignedIds(updated.assigned_precinct_ids || []); }
      alert('Precincts assigned successfully');
    } catch (err) {
      alert(err.response?.data?.detail || 'Assignment failed');
    }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="tl-assign-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Assign Precincts to Surveyors</h1>
        <p className="text-slate-500 text-sm mt-1">
          Assign your {myPrecincts.length} precincts to {surveyors.length} surveyors
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Surveyor List */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-emerald-500" /> My Surveyors
            </CardTitle>
          </CardHeader>
          <CardContent>
            {surveyors.length === 0 ? (
              <p className="text-slate-400 text-sm text-center py-4">No surveyors assigned to you</p>
            ) : (
              <div className="space-y-1">
                {surveyors.map(s => (
                  <button
                    key={s.id}
                    data-testid={`select-surveyor-${s.id}`}
                    onClick={() => selectSurveyor(s)}
                    className={`w-full text-left px-3 py-3 rounded-lg text-sm transition-all ${
                      selectedSurveyor?.id === s.id ? 'bg-blue-50 border border-blue-300' : 'hover:bg-slate-50 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{s.name}</p>
                        <p className="text-xs text-slate-400">@{s.username}</p>
                      </div>
                      <Badge className="text-xs bg-slate-100 text-slate-600">
                        {(s.assigned_precinct_ids || []).length} precincts
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Precinct Assignment */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg">
                {selectedSurveyor ? (
                  <>
                    <MapPin className="w-5 h-5 inline mr-2 text-blue-500" />
                    Precincts for {selectedSurveyor.name}
                  </>
                ) : 'Select a surveyor'}
              </CardTitle>
              {selectedSurveyor && (
                <Button
                  data-testid="save-tl-assignments-btn"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-slate-900 hover:bg-slate-800"
                  size="sm"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Save
                </Button>
              )}
            </div>
            {selectedSurveyor && (
              <p className="text-xs text-slate-500 mt-1">{assignedIds.length} of {myPrecincts.length} precincts selected</p>
            )}
          </CardHeader>
          <CardContent>
            {!selectedSurveyor ? (
              <div className="text-center py-12 text-slate-400">
                <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Select a surveyor from the left panel to assign precincts</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {myPrecincts.map(p => {
                  const isChecked = assignedIds.includes(p.id);
                  return (
                    <label
                      key={p.id}
                      data-testid={`tl-precinct-checkbox-${p.id}`}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        isChecked ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => togglePrecinct(p.id)}
                      />
                      <div>
                        <p className="font-mono text-sm font-medium text-slate-900">{p.precinct_number}</p>
                        {p.city_municipality && (
                          <p className="text-xs text-slate-500">{p.city_municipality}</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
